import { signal } from '@preact/signals';
import * as S from './state';

/**
 * One-time guidance. Every hint is shown once per browser and can be brought back from Settings.
 * The intro is a modal shown before the very first realm; the rest are toasts fired by the first
 * occurrence of the thing they explain.
 */
const KEY = 'knight.hints';

function load(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as Record<string, boolean>;
      if (v && typeof v === 'object') return v;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function save(v: Record<string, boolean>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

export function hintSeen(key: string): boolean {
  return !!load()[key];
}

export function markHint(key: string): void {
  const v = load();
  v[key] = true;
  save(v);
}

export function resetHints(): void {
  save({});
  hintsVersion.value++;
}

/** bumps when hints are reset so screens can re-read */
export const hintsVersion = signal(0);

/** Shows the text as a toast the first time the key is met. */
export function hintOnce(key: string, text: string): void {
  if (hintSeen(key)) return;
  markHint(key);
  S.showToast(text, 'hint');
}

/** The how-to-play modal: open with the action to run once the player has read it. */
export const introOpen = signal(false);
let introNext: (() => void) | null = null;

export function openIntro(next: () => void): void {
  introNext = next;
  introOpen.value = true;
}

export function closeIntro(proceed: boolean): void {
  markHint('intro');
  introOpen.value = false;
  const fn = introNext;
  introNext = null;
  if (proceed && fn) fn();
}

/** Runs the action, showing the intro first for a player who has never entered a realm. */
export function withIntro(next: () => void): void {
  if (hintSeen('intro')) next();
  else openIntro(next);
}
